/**
 * ReviewCheckoutPage
 * ==================
 * Client lands here from Claude after `submit_async_review` creates a
 * pending async_reviews row. Shows the artifact summary and redirects
 * to Stripe for payment. (P0-13 — the route was previously missing
 * and returned 404 from the MCP checkout_url.)
 *
 * Route: /review/:reviewId/checkout
 *
 * SEC-43: the API needs the buyer's session OR the signed checkout token
 * (`?t=`) that every server-issued checkout link carries. `api` attaches the
 * session and refreshes it on 401; the token is forwarded as-is. Without
 * either the API answers 404, so a signed-out visitor is offered sign-in.
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText,
  Clock,
  DollarSign,
  Brain,
  CreditCard,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  ArrowLeft,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/api";
import { getUserToken } from "@/lib/authStorage";

export default function ReviewCheckoutPage() {
  const { reviewId } = useParams();
  const [searchParams] = useSearchParams();
  const checkoutToken = searchParams.get("t");
  const navigate = useNavigate();

  const [checkout, setCheckout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/reviews/${reviewId}/checkout`, {
          params: checkoutToken ? { t: checkoutToken } : undefined,
        });
        if (!cancelled) setCheckout(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || "Review not found.");
          // Expired link or no link token: the buyer can still get in by
          // signing in to the account that submitted the review.
          if (err.response?.status === 404 && !getUserToken()) setNeedsSignIn(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reviewId, checkoutToken]);

  const handlePay = () => {
    if (!checkout?.stripe_checkout_url) return;
    setRedirecting(true);
    window.location.href = checkout.stripe_checkout_url;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-canvas flex flex-col items-center justify-center px-4 text-center">
        <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-ink mb-2">
          We couldn't find this review
        </h2>
        <p className="text-slateline-500 mb-6">
          {needsSignIn
            ? "This checkout link has expired or is incomplete. Sign in with the account that submitted the review to continue."
            : error}
        </p>
        {needsSignIn ? (
          <Button
            onClick={() =>
              navigate(`/login?return_to=${encodeURIComponent(`/review/${reviewId}/checkout`)}`)
            }
          >
            Sign in to continue
          </Button>
        ) : (
          <Button onClick={() => navigate("/dashboard")}>Go to Dashboard</Button>
        )}
      </div>
    );
  }

  if (checkout?.already_handled) {
    return (
      <div className="min-h-screen bg-canvas flex flex-col items-center justify-center px-4 text-center">
        <CheckCircle2 className="w-12 h-12 text-brand-green mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-ink mb-2">Already submitted</h2>
        <p className="text-slateline-500 mb-6">
          This review is currently <strong>{checkout.status}</strong> and no longer needs checkout.
        </p>
        <Button onClick={() => navigate("/dashboard")}>Go to My Reviews</Button>
      </div>
    );
  }

  const turnaroundLabel =
    checkout.turnaround_hours === 168
      ? "1 week"
      : checkout.turnaround_hours
      ? `${checkout.turnaround_hours}h`
      : "—";

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <header className="px-6 py-5 border-b border-slateline-200">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                if (window.history.length > 1) navigate(-1);
                else navigate("/dashboard");
              }}
              className="text-slateline-500 hover:text-ink p-1 -ml-1 transition-colors"
              title="Back"
              aria-label="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h1 className="text-xl font-semibold text-ink">Xpert</h1>
          </div>
          <span className="text-sm text-slateline-500">Async Review Checkout</span>
        </div>
      </header>

      <main className="flex-1 px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto"
        >
          <h2 className="text-2xl font-semibold text-ink mb-2">
            Review my AI output
          </h2>
          <p className="text-slateline-500 mb-8">
            A named reviewer will read your submission and write you a
            verdict within the turnaround window.
          </p>

          <div className="bg-canvas/50 border border-slateline-200 rounded-xl p-6 space-y-5">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-brand-green mt-0.5" />
              <div>
                <div className="text-sm text-slateline-500">Artifact</div>
                <div className="text-ink font-medium break-all">
                  {checkout.artifact_name || "—"}
                </div>
                <div className="text-xs text-slateline-400 mt-0.5">
                  {checkout.artifact_type || ""}
                </div>
              </div>
            </div>

            {checkout.expert_name && (
              <div className="flex items-start gap-3">
                <Brain className="w-5 h-5 text-brand-green mt-0.5" />
                <div>
                  <div className="text-sm text-slateline-500">Reviewer</div>
                  <div className="text-ink font-medium">{checkout.expert_name}</div>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-brand-green mt-0.5" />
              <div>
                <div className="text-sm text-slateline-500">Turnaround</div>
                <div className="text-ink font-medium">{turnaroundLabel}</div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <DollarSign className="w-5 h-5 text-brand-green mt-0.5" />
              <div>
                <div className="text-sm text-slateline-500">Total</div>
                <div className="text-ink text-xl font-semibold">
                  ${Number(checkout.price_usd || 0).toFixed(2)}
                </div>
              </div>
            </div>

            {checkout.context_preview?.user_goal && (
              <div className="pt-4 border-t border-slateline-200">
                <div className="text-xs text-slateline-500 mb-1">Your goal</div>
                <div className="text-sm text-ink italic">
                  "{checkout.context_preview.user_goal}"
                </div>
              </div>
            )}
          </div>

          <Button
            onClick={handlePay}
            disabled={!checkout.stripe_checkout_url || redirecting}
            className="w-full mt-6 h-12 bg-brand-green hover:bg-brand-greenHover text-white font-semibold"
          >
            {redirecting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <CreditCard className="w-5 h-5 mr-2" />
                Pay
              </>
            )}
          </Button>

          {!checkout.stripe_checkout_url && (
            <p className="text-xs text-amber-400 mt-3 text-center">
              Payment is temporarily unavailable. Please try again in a moment.
            </p>
          )}
        </motion.div>
      </main>
    </div>
  );
}
